import React, { useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

function FileComplaint({ currentUser }) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    fraudType: '',
    providerName: '',
    subject: '',
    incidentDate: '',
    amountInvolved: '',
    description: '',
    evidenceDescription: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Auto-populate user fields if logged in
  useEffect(() => {
    if (currentUser) {
      setFormData(prev => ({
        ...prev,
        name: currentUser.name || '',
        email: currentUser.email || '',
        phone: currentUser.phone || ''
      }));
    }
  }, [currentUser]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (isSubmitting) return;

    // Validate required fields manually (since we may bypass native form validation)
    const required = ['fraudType', 'providerName', 'subject', 'incidentDate', 'description', 'evidenceDescription'];
    for (const field of required) {
      if (!formData[field] || !String(formData[field]).trim()) {
        setSubmitError('Please fill in all required fields before submitting.');
        return;
      }
    }

    setIsSubmitting(true);
    setSubmitError('');
    setSubmitted(false);

    try {
      // Prepare complaint data for fraud-cases endpoint
      const complaintData = {
        name: formData.providerName,
        email: formData.email,
        phone: formData.phone,
        reported: formData.name,
        violation: formData.fraudType,
        status: 'Pending Review',
        severity: formData.amountInvolved ? 'High' : 'Medium',
        complaints: 1,
        warningLevel: 'New',
        description: `${formData.subject}\n\n${formData.description}\n\nEvidence: ${formData.evidenceDescription}\nIncident Date: ${formData.incidentDate}\nAmount Involved: ${formData.amountInvolved}`,
        lastKnownLocation: '',
        aliases: '',
        victimCount: 1,
        estimatedLoss: formData.amountInvolved || '0'
      };

      // Add userId if user is logged in
      if (currentUser && currentUser.id) {
        complaintData.userId = currentUser.id;
      }

      const response = await fetch(`${API_URL}/fraud-cases`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(complaintData)
      });

      const result = await response.json();

      if (result.success) {
        setSubmitted(true);
        // Reset form but keep user info if logged in
        setFormData({
          name: currentUser?.name || '',
          email: currentUser?.email || '',
          phone: currentUser?.phone || '',
          fraudType: '',
          providerName: '',
          subject: '',
          incidentDate: '',
          amountInvolved: '',
          description: '',
          evidenceDescription: ''
        });
        setTimeout(() => setSubmitted(false), 8000);
      } else {
        setSubmitError(result.message || 'Failed to submit. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting complaint:', error);
      setSubmitError('Network error. Please check your connection and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="tab-content">
      <h2>Report a Fraud</h2>

      <form onSubmit={(e) => { e.preventDefault(); return false; }} noValidate>
          {currentUser && (
            <div style={{
              backgroundColor: '#eef5ff',
              border: '1px solid #b7d4ff',
              borderRadius: '5px',
              padding: '12px 15px',
              marginBottom: '20px',
              fontSize: '14px',
              color: '#2c3e50'
            }}>
              Submitting as <strong>{currentUser.name}</strong> ({currentUser.email}). Updates will be sent to your registered email.
            </div>
          )}

          <div className="form-group">
            <label htmlFor="fraudType">Type of Fraud *</label>
            <select
              id="fraudType"
              name="fraudType"
              value={formData.fraudType}
              onChange={handleChange}
              required
            >
              <option value="">Select Fraud Type</option>
              <option value="plagiarism">Plagiarized Work Delivered</option>
              <option value="non-delivery">Non-Delivery of Services</option>
              <option value="fake-credentials">Fake Credentials/Documents</option>
              <option value="overcharging">Overcharging/Hidden Fees</option>
              <option value="data-manipulation">Data Manipulation/Fabrication</option>
              <option value="refund-denied">Refund Denied</option>
              <option value="impersonation">Provider Impersonation</option>
              <option value="other">Other</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="providerName">Fraudulent Provider Name/Company *</label>
            <input
              type="text"
              id="providerName"
              name="providerName"
              value={formData.providerName}
              onChange={handleChange}
              placeholder="Name of the fraudulent service provider"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="subject">Complaint Subject *</label>
            <input
              type="text"
              id="subject"
              name="subject"
              value={formData.subject}
              onChange={handleChange}
              placeholder="Brief summary of the fraud"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="incidentDate">Date of Incident *</label>
            <input
              type="date"
              id="incidentDate"
              name="incidentDate"
              value={formData.incidentDate}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="amountInvolved">Amount Involved (if applicable)</label>
            <input
              type="text"
              id="amountInvolved"
              name="amountInvolved"
              value={formData.amountInvolved}
              onChange={handleChange}
              placeholder="e.g., ₹25,000"
            />
          </div>

          <div className="form-group">
            <label htmlFor="description">Detailed Description of Fraud *</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows="6"
              placeholder="Provide a detailed account of what happened, including timeline, interactions, and how you were defrauded..."
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="evidenceDescription">Evidence Description *</label>
            <textarea
              id="evidenceDescription"
              name="evidenceDescription"
              value={formData.evidenceDescription}
              onChange={handleChange}
              rows="4"
              placeholder="Describe any evidence you have (emails, screenshots, contracts, transaction records, etc.). You will be contacted to submit actual files."
              required
            />
          </div>

          <div style={{
            backgroundColor: '#fff3cd',
            border: '1px solid #f39c12',
            borderRadius: '5px',
            padding: '15px',
            marginBottom: '20px'
          }}>
            <p style={{ fontSize: '14px', lineHeight: '1.6', margin: '0' }}>
              <strong>⚖️ Legal Notice:</strong> By submitting this complaint, you confirm that all
              information provided is true and accurate to the best of your knowledge. False reporting
              may result in legal consequences.
            </p>
          </div>

          <button
            type="button"
            className="submit-btn"
            onClick={handleSubmit}
            disabled={isSubmitting}
            style={{
              backgroundColor: isSubmitting ? '#95a5a6' : '#e74c3c',
              borderColor: isSubmitting ? '#95a5a6' : '#e74c3c',
              cursor: isSubmitting ? 'not-allowed' : 'pointer'
            }}
          >
            {isSubmitting ? 'Submitting...' : 'Submit Fraud Report'}
          </button>
        </form>

        {submitted && (
          <div className="success-message" style={{ marginTop: '20px' }}>
            Thank you! Your fraud report has been submitted successfully. Your Case ID will be sent to your email shortly. Our legal team will contact you within 2-3 business days to proceed with the investigation.
          </div>
        )}
        {submitError && (
          <div style={{
            backgroundColor: '#f8d7da',
            color: '#721c24',
            padding: '15px',
            borderRadius: '5px',
            marginTop: '20px',
            border: '1px solid #f5c6cb'
          }}>
            {submitError}
          </div>
        )}

    </div>
  );
}

export default FileComplaint;
